import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { LazyMotion, MotionConfig, domAnimation } from 'motion/react';
import { useEffect, useRef } from 'react';

import { refreshSession } from './api/auth-fetch';
import { refreshCoordinator } from './api/refresh-coordinator';
import { auth } from './api/endpoints';
import { I18nProvider } from './i18n/provider';
import { queryClient } from './lib/queryClient';
import { router } from './routes/router';
import { ToastHost } from './components/Toast';
import { useAuth } from './stores/auth';
import { watchSystemTheme } from './stores/theme';

/**
 * The boot sequence.
 *
 * The access token lives only in memory, so a hard reload starts with no
 * session and one refresh call decides whether there is one. `markReady()` runs
 * whatever the outcome, because the router waits on it before choosing between
 * a screen and the login page — leaving it unset on failure would hang the app
 * behind a skeleton forever.
 */
export function App() {
  /**
   * The boot runs once per mount, and the guard is a ref rather than the
   * `cancelled` flag it replaces.
   *
   * A cleanup flag stops the *result* of a duplicated effect from being applied;
   * it does not stop the requests. Under StrictMode that meant every page load
   * sent two `GET /auth/me`, and `/api/v1/auth` is rate limited at the edge
   * (control S-11) — so the cost of the duplicate was not tidiness, it was half
   * the budget of a screen that reloads. This is the same repair the email
   * verification screen already needed for the same reason.
   */
  const booted = useRef(false);

  useEffect(() => {
    if (booted.current) {
      return;
    }

    booted.current = true;

    void (async () => {
      const token = await refreshSession();

      if (token) {
        try {
          useAuth.getState().signIn(token, await auth.me());
        } catch {
          useAuth.getState().signOut();
        }
      }

      useAuth.getState().markReady();
    })();
  }, []);

  // Los access tokens que acuñan otras pestañas llegan por el canal y se
  // adoptan sin gastar la cookie: una pestaña en segundo plano con el token
  // caducado se actualiza sola y su siguiente petición ni ve el 401 — que era
  // la otra mitad de la carrera que quemaba la sesión (refresh-coordinator.ts).
  useEffect(() => refreshCoordinator.onToken((token) => useAuth.getState().setToken(token)), []);

  // Mientras el tema esté en «sistema», seguir al sistema también cuando
  // cambia. El primer valor ya lo fijó el script en línea de `index.html`.
  useEffect(watchSystemTheme, []);

  return (
    /*
      `LazyMotion` con `domAnimation` es lo que mantiene el presupuesto de
      `13 §8`: deja unos cinco kilobytes en el arranque en lugar de los treinta
      y pico del paquete entero, y por eso la interfaz usa `m.*` y nunca
      `motion.*` —`motion.div` arrastraría de vuelta todo lo que esto evita—.

      `reducedMotion="user"` respeta `prefers-reduced-motion` de una vez para
      todo el producto; los keyframes de CSS los apaga la regla equivalente de
      `index.css`.
    */
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user" transition={{ duration: 0.24, ease: [0.22, 0.8, 0.28, 1] }}>
        <QueryClientProvider client={queryClient}>
          <I18nProvider>
            <RouterProvider router={router} />
            <ToastHost />
          </I18nProvider>
        </QueryClientProvider>
      </MotionConfig>
    </LazyMotion>
  );
}
